import { functionalUpdate } from "@tanstack/react-table";
import { useQuery } from "@tanstack/react-query";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import qs from "qs";
import { isEmpty, get, pickBy, identity, mapValues } from "lodash";

import { queryClient } from "App";
import { ScrollColumn } from "components/BaseTableV3/ScrollColumn";
import {
  getFlattenedFilters,
  getTagFilters,
  getInitialColumnOrder,
  getInitialSort,
  getColumnVisibilityMap,
  defaultColumnPinning,
  isActionsColumn,
  sortToTableState,
  sortToQueryParams,
  paginationToQueryParams,
  buildColumnPinning,
  getFilterParams,
  paginationToTableState,
  defaultQueryParams,
} from "components/BaseTableV3/table-helpers";
import UserService from "services/User";

export const addCustomFieldsColumns = ({
  customFields = [],
  columns = [],
  visibleColumns = [],
}) => {
  if (!customFields.length) return columns;
  const columnsIds = columns.map(col => col.id);
  const customFieldsColumns = customFields.flatMap(cf =>
    cf.fields
      .filter(
        field =>
          // check if the column is not already in the columns array
          !columnsIds.includes(`${cf.accessorPrefix}.${field}`) &&
          // check if the column is visible
          visibleColumns.includes(`${cf.accessorPrefix}.${field}`)
      )
      .map(field => {
        const id = `${cf.accessorPrefix}.${field}`;
        return {
          header: `${cf.headerPrefix}.${field}`,
          id,
          meta: {
            isCustomField: true,
          },
          accessorFn: row => row?.[cf.accessorPrefix]?.[field],
          cell: ({ getValue }) => {
            const value = getValue();
            return Array.isArray(value) ? (
              <ScrollColumn>
                {value.map((v, i) => (
                  <li key={`${v}-${i}`}>{v}</li>
                ))}
              </ScrollColumn>
            ) : (
              value
            );
          },
        };
      })
  );
  return [...columns.slice(0, 1), ...customFieldsColumns, ...columns.slice(1)];
};

export const addResizedColumns = ({ columns = [], resizedColumns = {} }) => {
  return columns.map(col => {
    const size =
      resizedColumns[col.id] ??
      (col.accessorKey ? resizedColumns[col.accessorKey] : undefined);
    if (size) {
      return { ...col, size };
    }
    return col;
  });
};

const handleTableSettingsQuery = async (tableName, columns) => {
  const response = await UserService.getUserSettings();
  const tableSettings = get(
    response,
    `user_settings.preferences.tables.${tableName}`,
    {}
  );
  const visibleColumns = getInitialColumnOrder(
    tableSettings?.visible_columns,
    columns
  );

  const actionsColumn = columns.find(isActionsColumn);
  const freezedColumns =
    tableSettings.freezed_columns ?? (actionsColumn ? [actionsColumn.id] : []);
  const resizedColumns = tableSettings.resized_columns || {};
  const sort = getInitialSort(columns, tableSettings.sort);
  const pageSize = tableSettings.per_page || defaultQueryParams.per_page;

  const columnVisibility = getColumnVisibilityMap({ visibleColumns, columns });

  return {
    visibleColumns,
    freezedColumns,
    resizedColumns,
    pageSize,
    sort,
    columnVisibility,
  };
};

export const useTableConfig = ({
  tableName = "",
  columns = [],
  filters = [],
  defaultFilters = {},
}) => {
  const [searchParams, setSearchParams] = useSearchParams();

  const queryParams = useMemo(
    () => qs.parse(searchParams.toString()),
    [searchParams]
  );

  const setQueryParams = useCallback(
    (params, replace = true) => {
      const normalized = mapValues(params, p => (p === false ? "false" : p));
      const queryString = qs.stringify(pickBy(normalized, identity), {
        encodeValuesOnly: false,
        arrayFormat: "comma",
      });
      setSearchParams(queryString, { replace });
    },
    [setSearchParams]
  );
  const [filterParams, setFilterParams] = useState({});
  const [columnVisibility, setColumnVisibility] = useState({});
  const [columnPinning, setColumnPinning] = useState(defaultColumnPinning);
  const [columnOrder, setColumnOrder] = useState([]);
  const [selectedRows, setSelectedRows] = useState({});
  const [customFilters, setCustomFilters] = useState([]);
  const [initialized, setInitialized] = useState(false);
  const appliedSettings = useRef(null);
  const appliedPerPage = useRef(null);

  const handleFetchTableSettings = useCallback(async () => {
    return await handleTableSettingsQuery(tableName, columns);
  }, [tableName, columns]);

  const { data: tableSettings } = useQuery({
    queryKey: ["table-settings", tableName],
    queryFn: handleFetchTableSettings,
    enabled: columns.length > 0 && !!tableName,
  });

  // A remount is served the cached settings first and the refetched ones a beat
  // later, so keying off "have we initialized" would strand the table on stale
  // settings until a full page reload. Comparing content instead lets a genuine
  // change -- a reset, most visibly -- land while the table stays mounted.
  const settingsSignature = useMemo(
    () => (tableSettings ? JSON.stringify(tableSettings) : null),
    [tableSettings]
  );

  const handleSearchChange = useCallback(
    (search = "") => {
      setQueryParams({ ...queryParams, search: search ?? "", page: 1 });
    },
    [queryParams, setQueryParams]
  );

  const handleSortChange = useCallback(
    async updater => {
      const currentSort = sortToTableState(queryParams);
      const nextSort = functionalUpdate(updater, currentSort);
      const sortQueryParams = sortToQueryParams(nextSort);
      const nextQueryParams = { ...queryParams, ...sortQueryParams };
      setQueryParams(nextQueryParams);

      await UserService.updateUserTableSettings(
        tableName,
        "sort",
        sortQueryParams
      );
    },
    [queryParams, setQueryParams, tableName]
  );

  const handlePaginationChange = useCallback(
    async updater => {
      const nextQueryParams = functionalUpdate(updater, {
        pageIndex: parseInt(queryParams.page),
        pageSize: parseInt(queryParams.per_page),
      });
      const parsedQueryParams = paginationToQueryParams(nextQueryParams);

      setQueryParams({
        ...queryParams,
        ...parsedQueryParams,
      });

      if (
        parseInt(parsedQueryParams.per_page) !== parseInt(queryParams.per_page)
      ) {
        await UserService.updateUserTableSettings(
          tableName,
          "per_page",
          parseInt(parsedQueryParams.per_page)
        );
      }
    },
    [queryParams, setQueryParams, tableName]
  );

  const handleUpdateTableSettings = useCallback(
    async ({ visibleColumns, freezedColumns }) => {
      const columnVisibility = getColumnVisibilityMap({
        visibleColumns,
        columns,
      });

      setColumnVisibility(columnVisibility);

      setColumnOrder(visibleColumns);

      setColumnPinning(buildColumnPinning(freezedColumns, columns));

      await UserService.updateUserTableSettings(
        tableName,
        "visible_columns",
        visibleColumns
      );
      await UserService.updateUserTableSettings(
        tableName,
        "freezed_columns",
        freezedColumns
      );
      await queryClient.invalidateQueries({ queryKey: ["table-settings"] });
    },
    [tableName, columns]
  );

  const handleColumnOrder = async updater => {
    const nextColumnOrder = functionalUpdate(updater, columnOrder);
    setColumnOrder(nextColumnOrder);
    await UserService.updateUserTableSettings(
      tableName,
      "visible_columns",
      nextColumnOrder
    );

    await queryClient.invalidateQueries({ queryKey: ["table-settings"] });
  };

  // The new advanced search builds the legacy query params itself, so it only
  // needs them merged in alongside pagination/sort/search.
  const handleApplyAdvancedSearch = useCallback(
    params => {
      setQueryParams({
        ...params,
        page: 1,
        per_page: queryParams.per_page,
        sort_by: queryParams?.sort_by ?? null,
        sort_dir: queryParams?.sort_dir ?? null,
        search: queryParams?.search ?? "",
      });
    },
    [queryParams, setQueryParams]
  );

  const handleResizeColumnQuery = useCallback(
    async (resizedColumn = {}) => {
      const resizedColumns = {
        ...tableSettings.resizedColumns,
        ...resizedColumn,
      };
      await UserService.updateUserTableSettings(
        tableName,
        "resized_columns",
        resizedColumns
      );
      await queryClient.invalidateQueries({ queryKey: ["table-settings"] });
    },
    [tableSettings, tableName]
  );

  const handleUncheckAll = useCallback(async () => {
    setSelectedRows({});
  }, [setSelectedRows]);

  useEffect(() => {
    if (isEmpty(queryParams) || !filters.length) return;
    const flattenedFilters = getFlattenedFilters(filters);
    getFilterParams(queryParams, flattenedFilters)
      .then(({ filterParams, customFilters }) => {
        setFilterParams(filterParams);
        setCustomFilters(customFilters);
      })
      .catch(() => {});
  }, [queryParams, filters]);

  useEffect(() => {
    if (!tableSettings || appliedSettings.current === settingsSignature) return;
    const isFirstApply = appliedSettings.current === null;
    appliedSettings.current = settingsSignature;

    // A per_page in the URL (a typed value or a link) wins until the saved size
    // itself changes, so a reset still lands but a column resize does not
    // snap the page size back.
    const savedPerPage = tableSettings.pageSize || defaultQueryParams.per_page;
    const keepUrlPerPage =
      isFirstApply || savedPerPage === appliedPerPage.current;
    appliedPerPage.current = savedPerPage;
    const per_page = (keepUrlPerPage && queryParams.per_page) || savedPerPage;
    const freezedColumns = tableSettings?.freezedColumns || [];
    setColumnVisibility(tableSettings.columnVisibility);
    setColumnOrder(tableSettings?.visibleColumns || []);

    setColumnPinning(buildColumnPinning(freezedColumns, columns));
    // Links saved before disabled-tag visibility became a user preference still
    // carry the parameter; BaseTableV3 adds the preference to each request.
    const { show_disabled_tags: _retiredParam, ...currentQueryParams } =
      queryParams;
    const nextQueryParams = {
      page: 1,
      // Only seeded on the first apply: a re-apply must not resurrect default
      // filters the user has since cleared.
      ...(isFirstApply ? defaultFilters : {}),
      ...currentQueryParams,
      per_page,
    };
    if (!isEmpty(tableSettings.sort)) {
      nextQueryParams.sort_by = tableSettings.sort.sort_by;
      nextQueryParams.sort_dir = tableSettings.sort.sort_dir;
    }

    setQueryParams(nextQueryParams);
    setInitialized(true);
  }, [
    settingsSignature,
    tableSettings,
    columns,
    defaultFilters,
    queryParams,
    setQueryParams,
    setColumnVisibility,
    setColumnOrder,
    setColumnPinning,
  ]);

  return {
    queryParams,
    tableSettings,
    filterParams,
    search: queryParams?.search ?? "",
    sorting: sortToTableState(queryParams),
    pagination: paginationToTableState(queryParams),
    tagsFilters: getTagFilters(queryParams?.tags),
    columnVisibility,
    columnOrder,
    columnPinning,
    selectedRows,
    customFilters,
    initialized,
    setSelectedRows,
    setColumnVisibility,
    setColumnPinning,
    setColumnOrder: handleColumnOrder,
    setPagination: handlePaginationChange,
    setSort: handleSortChange,
    setSearch: handleSearchChange,
    handleUpdateTableSettings,
    handleApplyAdvancedSearch,
    handleResizeColumnQuery,
    handleUncheckAll,
  };
};
